import {
    ActionReducerMapBuilder,
    Draft,
    SerializedError,
    createAsyncThunk,
    createSlice,
} from '@reduxjs/toolkit';
import { IAction, SlicePropsWithData } from '../store';
import { urlExcelUploadTemplate, urlExcelUploadTemplates } from '../../Constants';
import { safeJsonParse } from '../../util/DataUtils';
import { downloadDocumentsStream } from '../../util/FileUtils';
import { apiFetch } from '../../util/ProjectUtils';

const initialState: IProjectExcelUploadState = {
    isDialogOpen: false,
    data: null,
    loading: false,
    downloading: false,
    uploading: false,
    error: null,
};

const reducers = {
    showExcelUploadDialog(state: IProjectExcelUploadState) {
        state.isDialogOpen = true;
    },
    closeExcelUploadDialog(state: IProjectExcelUploadState) {
        state.isDialogOpen = false;
    },
    resetState() {
        return initialState;
    },
};

const thunks = {
    fetchExcelTemplates: async (replicaId: string): Promise<IFetchExcelTemplatesResponse> => {
        try {
            const request: RequestInit = {
                headers: {
                    replicaId,
                },
            };
            const response = await apiFetch(urlExcelUploadTemplates, request);

            if (!response.ok) {
                return Promise.reject(new Error(await response.text()));
            }

            return safeJsonParse<IFetchExcelTemplatesResponse>(await response.text(), {
                templates: [],
            });
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    downloadExcelTemplate: async (params: {
        replicaId: string;
        template: IExcelTemplate;
    }): Promise<void> => {
        try {
            const { replicaId, template } = params;
            const request: RequestInit = {
                headers: {
                    replicaId,
                    key: template.key,
                },
            };
            const response = await apiFetch(urlExcelUploadTemplate, request);

            if (!response.ok) {
                return Promise.reject(new Error(await response.text()));
            }

            downloadDocumentsStream(await response.blob(), `${template.subject}.xlsx`);
            return Promise.resolve();
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    uploadExcelTemplate: async (params: {
        replicaId: string;
        excelFile: File;
        errorFileName: string;
    }): Promise<void> => {
        try {
            const { replicaId, excelFile, errorFileName } = params;

            const formData = new FormData();
            formData.append('excelFile', excelFile);

            const request: RequestInit = {
                method: 'POST',
                headers: {
                    replicaId,
                    checkData: 'true',
                },
                body: formData,
            };

            const response = await apiFetch(urlExcelUploadTemplate, request);

            if (!response.ok) {
                return Promise.reject(new Error(await response.text()));
            }

            if (response.status === 200) {
                // File result contains Excel with errors
                downloadDocumentsStream(await response.blob(), `${errorFileName}.xlsx`);
                return Promise.reject();
            }

            return Promise.resolve(); // 201 (No content)
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    _reducers: (builder: ActionReducerMapBuilder<IProjectExcelUploadState>) => {
        builder.addCase(fetchExcelTemplates.pending, (state: Draft<IProjectExcelUploadState>) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(
            fetchExcelTemplates.fulfilled,
            (
                state: Draft<IProjectExcelUploadState>,
                action: IAction<IFetchExcelTemplatesResponse>,
            ) => {
                state.data = action.payload.templates;
                state.loading = false;
                state.error = null;
            },
        );
        builder.addCase(
            fetchExcelTemplates.rejected,
            (state: Draft<IProjectExcelUploadState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );

        builder.addCase(downloadExcelTemplate.pending, (state: Draft<IProjectExcelUploadState>) => {
            state.downloading = true;
            state.error = null;
        });
        builder.addCase(
            downloadExcelTemplate.fulfilled,
            (state: Draft<IProjectExcelUploadState>) => {
                state.downloading = false;
                state.error = null;
            },
        );
        builder.addCase(
            downloadExcelTemplate.rejected,
            (state: Draft<IProjectExcelUploadState>, action: IAction<SerializedError>) => {
                state.downloading = false;
                state.error = action.error;
            },
        );

        builder.addCase(uploadExcelTemplate.pending, (state: Draft<IProjectExcelUploadState>) => {
            state.uploading = true;
            state.error = null;
        });
        builder.addCase(uploadExcelTemplate.fulfilled, (state: Draft<IProjectExcelUploadState>) => {
            state.uploading = false;
            state.error = null;
        });
        builder.addCase(
            uploadExcelTemplate.rejected,
            (state: Draft<IProjectExcelUploadState>, action: IAction<SerializedError>) => {
                state.uploading = false;
                state.error = action.error;
            },
        );
    },
};

export const fetchExcelTemplates = createAsyncThunk(
    'excelupload/fetchExcelTemplates',
    thunks.fetchExcelTemplates,
);

export const downloadExcelTemplate = createAsyncThunk(
    'excelupload/downloadExcelTemplate',
    thunks.downloadExcelTemplate,
);

export const uploadExcelTemplate = createAsyncThunk(
    'excelupload/uploadExcelTemplate',
    thunks.uploadExcelTemplate,
);

interface IFetchExcelTemplatesResponse {
    templates: IExcelTemplate[];
}

export interface IExcelTemplate {
    key: string;
    subject: string;
}

export interface IProjectExcelUploadState extends SlicePropsWithData<IExcelTemplate[]> {
    downloading: boolean;
    uploading: boolean;
    isDialogOpen: boolean;
}

export const projectExcelUploadSlice = createSlice({
    name: 'projectExcelUpload',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

export const { showExcelUploadDialog, closeExcelUploadDialog, resetState } =
    projectExcelUploadSlice.actions;

export default projectExcelUploadSlice.reducer;
